/**
 * Broker API client — Discovery, Connections, Leads, Applications
 * Epic 3: Broker Discovery + Connection
 * Epic 3B: Broker Application Workflow
 */

import { authenticatedFetch } from './authApi'

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    const detail = err.detail
    const message =
      typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join('; ') || `HTTP ${res.status}`
          : `HTTP ${res.status}`
    throw new Error(message)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

async function extractCsvBlob(res: Response): Promise<Blob> {
  const contentType = res.headers.get('content-type') || ''
  if (contentType.includes('text/csv')) return res.blob()
  // Proxy may have wrapped CSV in JSON — unwrap
  const text = await res.text()
  try {
    const json = JSON.parse(text)
    const csv = typeof json.detail === 'string' ? json.detail : text
    return new Blob([csv], { type: 'text/csv' })
  } catch {
    return new Blob([text], { type: 'text/csv' })
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Broker {
  id: string
  email: string
  business_name: string | null
  license_number: string | null
  years_experience: number | null
  specialisation: string | null
  bio: string | null
  photo_url: string | null
  is_accepting_clients: boolean
  max_clients: number | null
  is_master_broker: boolean
  can_add_customers?: boolean
}

export interface BrokersResponse {
  brokers: Broker[]
  count: number
  auto_route: boolean
  waitlist: boolean
}

export interface Connection {
  id: string
  customer_id: string
  broker_id: string
  status: string
  comparison_snapshot?: Record<string, unknown> | null
  created_at: string | null
  broker_name?: string | null
  customer_name?: string | null
  customer_email?: string | null
  message?: string | null
  linked_leads?: number
}

export interface CustomerProfile {
  connection_id: string
  customer_id: string
  connection_status: string
  comparison_snapshot?: Record<string, unknown> | null
  email: string | null
  full_name: string | null
  first_name: string | null
  last_name: string | null
  phone_number: string | null
  date_of_birth: string | null
  address: string | null
  annual_income: number | null
  employment_status: string | null
  dependants: number | null
}

export interface ConnectionsResponse {
  connections: Connection[]
  count: number
}

export interface Lead {
  id: string
  name: string | null
  email: string | null
  phone: string | null
  loan_amount: number | null
  property_value: number | null
  loan_purpose: string | null
  status: string
  notes: string | null
  comparison_snapshot?: Record<string, unknown> | null
  preferred_contact_method: string | null
  preferred_contact_time: string | null
  created_at: string | null
  contacted_at: string | null
  lead_price?: number | null
  is_billed?: boolean
  is_repeat_enquiry?: boolean
  customer_type?: string | null
  source?: string | null
  converted_at?: string | null
  assigned_broker_id?: string | null
  converted_user_id?: string | null
}

export interface LeadStats {
  total: number
  contacted: number
  converted: number
  dead: number
  contacted_rate: number
  conversion_rate: number
  revenue_owed: number
  revenue_billed: number
}

export interface AdminConfig {
  master_broker_id?: string | null
  lead_price_aud?: string
  broker_sla_hours?: string
}

// ── Public Broker Discovery ──────────────────────────────────────────────────

export async function getBrokers(specialisation?: string): Promise<BrokersResponse> {
  const params = specialisation ? `?specialisation=${encodeURIComponent(specialisation)}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/brokers${params}`)
  return handleResponse<BrokersResponse>(res)
}

export async function getBroker(id: string): Promise<Broker> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/brokers/${id}`)
  return handleResponse<Broker>(res)
}

// ── Connections (Authenticated) ──────────────────────────────────────────────

export async function createConnection(
  token: string,
  body: { broker_id: string; message?: string; comparison_snapshot?: Record<string, unknown> },
): Promise<Connection> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/connections`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<Connection>(res)
}

export async function getMyConnections(token: string): Promise<ConnectionsResponse> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/connections/my`, {
    headers: authHeaders(token),
  })
  return handleResponse<ConnectionsResponse>(res)
}

export async function updateConnection(
  token: string,
  id: string,
  action: 'accept' | 'reject',
): Promise<Connection> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/connections/${id}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ action }),
  })
  return handleResponse<Connection>(res)
}

export async function deleteConnection(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/connections/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

export async function getConnectionCustomer(
  token: string,
  connectionId: string,
): Promise<CustomerProfile> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/connections/${connectionId}/customer`, {
    headers: authHeaders(token),
  })
  return handleResponse<CustomerProfile>(res)
}

// ── Waitlist ─────────────────────────────────────────────────────────────────

export async function joinWaitlist(
  body: { email: string; loan_amount?: number; loan_purpose?: string },
): Promise<{ message: string; id: string; status: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  return handleResponse(res)
}

// ── Broker Lead Views ────────────────────────────────────────────────────────

export async function getBrokerLeads(
  token: string,
  status?: string,
): Promise<{ leads: Lead[]; count: number }> {
  const params = status ? `?status=${encodeURIComponent(status)}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/broker/leads${params}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function updateLeadStatus(
  token: string,
  id: string,
  status: string,
): Promise<{ id: string; status: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/broker/leads/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ status }),
  })
  return handleResponse(res)
}

// ── Admin Lead Management ────────────────────────────────────────────────────

export async function getAdminLeads(
  token: string,
  filters?: { status?: string; broker_id?: string; date_from?: string; date_to?: string },
): Promise<{ items: Lead[]; count: number }> {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.broker_id) params.set('broker_id', filters.broker_id)
  if (filters?.date_from) params.set('date_from', filters.date_from)
  if (filters?.date_to) params.set('date_to', filters.date_to)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads${qs}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function getLeadStats(token: string): Promise<LeadStats> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads/stats`, {
    headers: authHeaders(token),
  })
  return handleResponse<LeadStats>(res)
}

export async function exportLeadsCsv(token: string): Promise<Blob> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads/export`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return extractCsvBlob(res)
}

export async function updateAdminLeadStatus(
  token: string,
  id: string,
  status: string,
): Promise<{ id: string; status: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ status }),
  })
  return handleResponse(res)
}

export async function batchBillLeads(
  token: string,
  leadIds: string[],
): Promise<{ billed_count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads/batch-bill`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ lead_ids: leadIds }),
  })
  return handleResponse(res)
}

export async function deleteLead(
  token: string,
  leadId: string,
): Promise<{ deleted: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads/${leadId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function clearAllLeads(
  token: string,
): Promise<{ deleted_count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/leads`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function setMasterBroker(token: string, brokerId: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/config/master-broker`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ value: brokerId }),
  })
  await handleResponse(res)
}

export async function setLeadPrice(token: string, price: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/config/lead-price`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ value: price }),
  })
  await handleResponse(res)
}

export async function getAdminConfig(token: string): Promise<AdminConfig> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/config`, {
    headers: authHeaders(token),
  })
  return handleResponse<AdminConfig>(res)
}

export async function getAdminBrokers(token: string): Promise<BrokersResponse> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/brokers`, {
    headers: authHeaders(token),
  })
  return handleResponse<BrokersResponse>(res)
}

// ── Admin Broker Profile Edit ────────────────────────────────────────────────

export async function updateAdminBrokerProfile(
  token: string,
  brokerId: string,
  updates: {
    business_name?: string
    license_number?: string
    years_experience?: number
    specialization?: string
    bio?: string
    is_accepting_clients?: boolean
    is_master_broker?: boolean
    can_add_customers?: boolean
  },
): Promise<{ id: string; updated: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/brokers/${brokerId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(updates),
  })
  return handleResponse(res)
}

// ── Admin Connections View ───────────────────────────────────────────────────

export interface AdminConnection {
  id: string
  broker_id: string
  customer_id: string | null
  status: string
  created_at: string | null
  broker_name: string | null
  customer_name: string | null
}

export async function getAdminConnections(
  token: string,
): Promise<{ connections: AdminConnection[]; count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/connections`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function updateAdminConnectionStatus(
  token: string,
  connectionId: string,
  status: string,
): Promise<{ id: string; status: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/connections/${connectionId}`, {
    method: 'PATCH',
    headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  })
  return handleResponse(res)
}

export async function deleteAdminConnection(
  token: string,
  connectionId: string,
): Promise<{ deleted: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/connections/${connectionId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function deleteCustomer(
  token: string,
  customerId: string,
): Promise<{ deleted: boolean; customer_id: string; customer_name: string; original_email: string; cleanup: { connections: number; applications: number; profile: boolean; tokens: number } }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/customers/${customerId}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

// ── Admin Customer List ─────────────────────────────────────────────────────

export interface AdminCustomer {
  id: string
  email: string
  full_name: string
  is_active: boolean
  created_at: string | null
  phone_number: string | null
  active_connections: number
  active_applications: number
}

export async function listCustomers(
  token: string,
  search?: string,
): Promise<{ customers: AdminCustomer[]; count: number }> {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/customers${params}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

// ── Admin Connection History ─────────────────────────────────────────────────

export interface ConnectionHistoryApp {
  id: string
  stage: string
  lender_name: string | null
  loan_amount: number | null
  loan_purpose: string | null
  outcome: string | null
  created_at: string | null
  updated_at: string | null
  is_active: boolean
  reference_number: string | null
}

export interface ConnectionHistoryComm {
  id: string
  application_id: string
  template_name: string
  recipient_email: string
  recipient_name: string | null
  subject: string | null
  status: string
  payload: Record<string, unknown>
  created_at: string | null
  sent_at: string | null
  attempts: number
  last_error: string | null
}

export interface ConnectionHistoryStage {
  id: string
  application_id: string
  from_stage: string | null
  to_stage: string
  changed_by: string | null
  notes: string | null
  changed_at: string | null
}

export interface ConnectionHistory {
  connection: AdminConnection & {
    message: string | null
    broker_email: string | null
    customer_email: string | null
  }
  applications: ConnectionHistoryApp[]
  communications: ConnectionHistoryComm[]
  stage_history: ConnectionHistoryStage[]
}

export async function getConnectionHistory(
  token: string,
  connectionId: string,
): Promise<ConnectionHistory> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/connections/${connectionId}/history`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

// ── Password Reset (Admin) ───────────────────────────────────────────────────

export async function resetPassword(
  token: string,
  username: string,
  newPassword: string,
): Promise<{ success: boolean; username: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/reset-password`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ username, new_password: newPassword }),
  })
  return handleResponse(res)
}

// ═══════════════════════════════════════════════════════════════════════════════
// EPIC 3B: Applications
// ═══════════════════════════════════════════════════════════════════════════════

export interface Application {
  id: string
  connection_id: string | null
  broker_id: string
  customer_id: string
  stage: string
  stage_notes: string | null
  stage_updated_at: string | null
  lender_name: string | null
  lender_reference: string | null
  loan_amount: number | null
  property_value: number | null
  loan_purpose: string | null
  property_address: string | null
  pre_approval_expiry: string | null
  letter_of_offer_expiry: string | null
  settlement_date: string | null
  submitted_at: string | null
  settled_at: string | null
  outcome: string | null
  outcome_notes: string | null
  decline_reason: string | null
  lmi_required: boolean
  lmi_approved: boolean | null
  is_active: boolean
  created_at: string | null
  updated_at: string | null
  customer_name: string | null
  customer_email?: string | null
  customer_phone?: string | null
  customer_address?: string | null
  customer_employment?: string | null
  customer_income?: number | null
  customer_dependants?: number | null
  broker_name?: string | null
  business_name?: string | null
  broker_notes?: string | null
}

export interface ApplicationsResponse {
  applications: Application[]
  count: number
}

export interface StageHistoryEntry {
  id: string
  from_stage: string | null
  to_stage: string
  notes: string | null
  changed_at: string | null
  changed_by_name: string | null
}

export interface ApplicationCondition {
  id: string
  description: string
  is_cleared: boolean
  cleared_at: string | null
  cleared_notes: string | null
  created_at: string | null
}

export interface CommunicationEntry {
  id: string
  template_name: string
  recipient_email: string
  recipient_name: string | null
  subject: string | null
  status: string
  payload: Record<string, unknown>
  created_at: string | null
  sent_at: string | null
  attempts: number
  last_error: string | null
}

export interface CustomerApplication {
  id: string
  stage: string
  stage_notes: string | null
  stage_updated_at: string | null
  lender_name: string | null
  loan_amount: number | null
  property_address: string | null
  outcome: string | null
  is_active: boolean
  created_at: string | null
  pre_approval_expiry: string | null
  settlement_date: string | null
  broker_name: string | null
  business_name: string | null
}

export interface WorkflowStatus {
  health: { status: string; [key: string]: unknown }
  flows: { id: string; label: string; disabled: boolean; info: string }[]
  node_red_url: string
}

// ── Application CRUD ─────────────────────────────────────────────────────────

export async function createApplication(
  token: string,
  body: { connection_id: string; loan_purpose?: string; notes?: string },
): Promise<Application> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<Application>(res)
}

export async function listApplications(
  token: string,
  filters?: { stage?: string; active?: boolean },
): Promise<ApplicationsResponse> {
  const params = new URLSearchParams()
  if (filters?.stage) params.set('stage', filters.stage)
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications${qs}`, {
    headers: authHeaders(token),
  })
  return handleResponse<ApplicationsResponse>(res)
}

export async function getApplication(
  token: string,
  id: string,
): Promise<Application> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}`, {
    headers: authHeaders(token),
  })
  return handleResponse<Application>(res)
}

export async function updateApplicationStage(
  token: string,
  id: string,
  stage: string,
  notes?: string,
): Promise<{ id: string; from_stage: string; stage: string; stage_updated_at: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/stage`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ stage, notes }),
  })
  return handleResponse(res)
}

export async function updateApplicationDetails(
  token: string,
  id: string,
  details: {
    lender_name?: string
    lender_reference?: string
    loan_amount?: number
    property_value?: number
    loan_purpose?: string
    property_address?: string
    pre_approval_expiry?: string
    settlement_date?: string
    lmi_required?: boolean
    broker_notes?: string
  },
): Promise<{ id: string; lender_name: string | null; loan_amount: number | null; updated_at: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/details`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify(details),
  })
  return handleResponse(res)
}

// ── Archive & Admin ─────────────────────────────────────────────────────────

export async function archiveApplication(
  token: string,
  id: string,
): Promise<{ id: string; is_active: boolean }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/archive`, {
    method: 'PUT',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function reactivateApplication(
  token: string,
  id: string,
  stage: string,
  notes?: string,
): Promise<{ id: string; from_stage: string; stage: string; stage_updated_at: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/reactivate`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ stage, notes }),
  })
  return handleResponse(res)
}

export async function listAllApplications(
  token: string,
  filters?: { stage?: string; active?: boolean; search?: string },
): Promise<ApplicationsResponse> {
  const params = new URLSearchParams()
  if (filters?.stage) params.set('stage', filters.stage)
  if (filters?.active !== undefined) params.set('active', String(filters.active))
  if (filters?.search) params.set('search', filters.search)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/admin/all${qs}`, {
    headers: authHeaders(token),
  })
  return handleResponse<ApplicationsResponse>(res)
}

export async function deleteApplication(
  token: string,
  id: string,
): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── History & Conditions ─────────────────────────────────────────────────────

export async function getApplicationHistory(
  token: string,
  id: string,
): Promise<{ history: StageHistoryEntry[]; count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/history`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function getApplicationCommunications(
  token: string,
  id: string,
): Promise<{ communications: CommunicationEntry[]; count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${id}/communications`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function addApplicationCondition(
  token: string,
  appId: string,
  description: string,
): Promise<ApplicationCondition> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${appId}/conditions`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify({ description }),
  })
  return handleResponse<ApplicationCondition>(res)
}

export async function clearApplicationCondition(
  token: string,
  appId: string,
  conditionId: string,
  clearedNotes?: string,
): Promise<ApplicationCondition> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${appId}/conditions/${conditionId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ cleared_notes: clearedNotes }),
  })
  return handleResponse<ApplicationCondition>(res)
}

export async function getApplicationConditions(
  token: string,
  appId: string,
): Promise<{ conditions: ApplicationCondition[]; count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/${appId}/conditions`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

// ── Customer View ────────────────────────────────────────────────────────────

export async function getCustomerApplication(
  token: string,
): Promise<CustomerApplication> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/customer/me`, {
    headers: authHeaders(token),
  })
  return handleResponse<CustomerApplication>(res)
}

// ── Admin Workflow Controls ──────────────────────────────────────────────────

export async function getWorkflowStatus(
  token: string,
): Promise<WorkflowStatus> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/workflow/status`, {
    headers: authHeaders(token),
  })
  return handleResponse<WorkflowStatus>(res)
}

export async function toggleWorkflowFlow(
  token: string,
  flowId: string,
  disabled: boolean,
): Promise<{ id: string; label: string; disabled: boolean; message: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/workflow/flows/${flowId}`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ disabled }),
  })
  return handleResponse(res)
}


// ── Admin Manual Triggers (email cron) ──────────────────────────────────────

export async function triggerStaleCheck(token: string): Promise<{ message: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/admin/trigger-stale-check`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function triggerExpiryCheck(token: string): Promise<{ message: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/admin/trigger-expiry-check`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function triggerEmailSend(token: string): Promise<{ message: string }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/applications/admin/trigger-email-send`, {
    method: 'POST',
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

// ═══════════════════════════════════════════════════════════════════════════════
// EPIC 3C: Broker-Initiated Customer Onboarding
// ═══════════════════════════════════════════════════════════════════════════════

export interface BrokerCustomer {
  connection_id: string
  customer_id: string | null
  name: string | null
  email: string
  phone: string | null
  status: string
  initiated_by: string
  connected_at: string | null
  has_active_application: boolean
}

export interface BrokerConnectionResult {
  id: string
  customer_id: string | null
  broker_id: string
  status: string
  initiated_by: string
  invite_email?: string
  invite_name?: string
  customer_name?: string
  created_at: string | null
  linked_leads?: number
}

export async function getBrokerCustomers(
  token: string,
  search?: string,
): Promise<{ customers: BrokerCustomer[]; count: number }> {
  const params = search ? `?search=${encodeURIComponent(search)}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/broker/connections/customers${params}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function brokerCreateConnection(
  token: string,
  body: { email: string; name?: string; phone?: string; lead_id?: string },
): Promise<BrokerConnectionResult> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/broker/connections`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<BrokerConnectionResult>(res)
}

// ═══════════════════════════════════════════════════════════════════════════════
// EPIC 4.1: Billing v1
// ═══════════════════════════════════════════════════════════════════════════════

export interface BillingBrokerSummary {
  broker_id: string
  broker_email: string
  business_name: string | null
  total_leads: number
  billable_leads: number
  billed_leads: number
  total_owed: number
  total_billed: number
  outstanding: number
}

export interface BillingSummaryResponse {
  month: string
  items: BillingBrokerSummary[]
  count: number
}

export interface Settlement {
  id: string
  application_id: string
  broker_id: string
  loan_amount: number | null
  broker_commission: number | null
  platform_percentage: number | null
  platform_fee: number | null
  status: string
  settlement_date: string | null
  notes: string | null
  created_at: string | null
  updated_at: string | null
  customer_name?: string | null
  broker_name?: string | null
}

export async function getBillingSummary(
  token: string,
  month?: string,
): Promise<BillingSummaryResponse> {
  const params = month ? `?month=${encodeURIComponent(month)}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/billing/summary${params}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function exportBillingCsv(
  token: string,
  brokerId: string,
  month?: string,
): Promise<Blob> {
  const params = new URLSearchParams({ broker_id: brokerId })
  if (month) params.set('month', month)
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/billing/export?${params.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return extractCsvBlob(res)
}

export async function getBillingLeadDetails(
  token: string,
  brokerId: string,
  month?: string,
  filter?: 'all' | 'billable',
): Promise<Lead[]> {
  const params = new URLSearchParams({ broker_id: brokerId })
  if (month) params.set('month', month)
  if (filter) params.set('filter', filter)
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/billing/leads?${params.toString()}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function createSettlement(
  token: string,
  body: {
    application_id: string
    broker_id: string
    loan_amount?: number
    broker_commission?: number
    settlement_date?: string
    notes?: string
  },
): Promise<Settlement> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/settlements`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<Settlement>(res)
}

export async function listSettlements(
  token: string,
  filters?: { broker_id?: string; status?: string; month?: string },
): Promise<{ items: Settlement[]; count: number }> {
  const params = new URLSearchParams()
  if (filters?.broker_id) params.set('broker_id', filters.broker_id)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.month) params.set('month', filters.month)
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/settlements${qs}`, {
    headers: authHeaders(token),
  })
  return handleResponse(res)
}

export async function getSettlement(
  token: string,
  id: string,
): Promise<Settlement> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/settlements/${id}`, {
    headers: authHeaders(token),
  })
  return handleResponse<Settlement>(res)
}

export async function updateSettlementStatus(
  token: string,
  id: string,
  status: string,
): Promise<Settlement> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/settlements/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ status }),
  })
  return handleResponse<Settlement>(res)
}

export async function deleteSettlement(
  token: string,
  id: string,
): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/settlements/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

export async function updateCommissionRate(
  token: string,
  rate: number,
): Promise<{ platform_commission_pct: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/config/commission-rate`, {
    method: 'PATCH',
    headers: authHeaders(token),
    body: JSON.stringify({ rate }),
  })
  return handleResponse(res)
}

// ── Broker Comparison Notes — REQ-BRKR-007 ──────────────────────────────────

export async function getBrokerClientComparisons(
  token: string,
  customerId: string,
): Promise<{ comparisons: unknown[]; count: number }> {
  const res = await authenticatedFetch(
    `${GATEWAY_BASE}/api/broker/clients/${encodeURIComponent(customerId)}/comparisons`,
    { headers: authHeaders(token) },
  )
  return handleResponse(res)
}

export async function upsertBrokerComparisonNote(
  token: string,
  comparisonId: string,
  body: { notes: string; rating?: number },
): Promise<{ id: string; comparison_id: string; notes: string; rating?: number; created_at?: string; updated_at?: string }> {
  const res = await authenticatedFetch(
    `${GATEWAY_BASE}/api/broker/comparisons/${encodeURIComponent(comparisonId)}/notes`,
    {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(body),
    },
  )
  return handleResponse(res)
}

export async function deleteBrokerComparisonNote(
  token: string,
  comparisonId: string,
  noteId: string,
): Promise<void> {
  const res = await authenticatedFetch(
    `${GATEWAY_BASE}/api/broker/comparisons/${encodeURIComponent(comparisonId)}/notes/${encodeURIComponent(noteId)}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  )
  return handleResponse(res)
}
